import { type ReactElement } from 'react';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { Button } from '@/components/ui/button';
import { Platform } from '@/lib/platform';
import { ExternalLink } from 'lucide-react';

interface CursorInstallStepsProps {
  detectedPlatform: Platform | null;
}

/**
 * Install / configure / uninstall body for the Kirin Cursor extension.
 * Rendered inside the "Kirin for Cursor" setup frame on the Get Started screen.
 */
export function CursorInstallSteps({
  detectedPlatform,
}: CursorInstallStepsProps): ReactElement {
  const isMac = detectedPlatform === Platform.DARWIN;
  const installCommand = isMac ? 'Cmd+Shift+P' : 'Ctrl+Shift+P';
  return (
    <>
      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
          Install Kirin Cursor
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="font-medium">Option A — Search in Cursor</div>
            <ol className="list-decimal ml-5 text-sm space-y-2">
              <li>Open Cursor → Extensions</li>
              <li>
                Search for <strong>"Kirin"</strong>
              </li>
              <li>
                Click <em>Install</em>
              </li>
            </ol>
          </div>
          <div className="space-y-3">
            <div className="font-medium">Option B — Direct Install</div>
            <Button asChild className="w-full whitespace-normal">
              <a
                href="https://open-vsx.org/extension/Knostic/kirin-openvsx"
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-w-0 items-center justify-center gap-2 text-center"
              >
                <ExternalLink className="h-4 w-4 flex-shrink-0" />
                <span className="min-w-0">
                  Download Kirin from Cursor's Extensions Marketplace
                </span>
              </a>
            </Button>
            <div className="text-xs text-muted-foreground">
              Click "Download" on the marketplace page, then install the VSIX in
              Cursor using {installCommand} → "Extensions: Install from VSIX"
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
          Configure & Verify
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="font-medium">Set Your API Key</div>
            <ol className="list-decimal ml-5 text-sm space-y-2">
              <li>In Cursor, open Command Palette (⌘/Ctrl+Shift+P)</li>
              <li>
                Run{' '}
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  Kirin: Set API Key
                </code>
              </li>
              <li>Paste your key and save</li>
            </ol>
          </div>
          <div className="space-y-3">
            <div className="font-medium">Test the Connection</div>
            <ol className="list-decimal ml-5 text-sm space-y-2">
              <li>
                Open a project with{' '}
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  .cursor/
                </code>{' '}
                configs
              </li>
              <li>
                Run{' '}
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  Kirin: Scan Now
                </code>
              </li>
              <li>
                Check the <strong>Alerts</strong> tab for results
              </li>
            </ol>
          </div>
        </div>

        <HighlightBox variant="info" padding="lg" className="mt-6">
          <div className="text-sm">
            <div className="font-medium mb-2">What happens next?</div>
            <div className="text-muted-foreground">
              Once configured, Kirin will monitor your workspace in real-time.
              Use the <strong>Policies</strong> tab to customize security rules
              and the <strong>Alerts</strong> tab to review findings.
            </div>
          </div>
        </HighlightBox>
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
          To Uninstall:
        </h3>
        <ol className="list-decimal ml-5 text-sm space-y-2">
          <li>Open Cursor → Extensions</li>
          <li>
            Search for <strong>"Kirin"</strong>
          </li>
          <li>
            Click the gear icon next to the extension and select{' '}
            <strong>Uninstall</strong>
          </li>
        </ol>
      </div>
    </>
  );
}
